/**
 * A noop function that does nothing.
 */
export const noop = () => {};

/**
 * Checks if a value is null, empty, or undefined.
 * @param v - The value to check.
 * @returns `true` if the value is null, empty, or undefined; otherwise, `false`.
 */
export const isNullOrEmpty = (v: unknown) =>
  v === null || v === '' || v === undefined;

/**
 * Checks if a value is not null or undefined.
 * Useful for filtering out elements from an array and telling TypeScript that the value is not null or undefined.
 *
 * @param value - The value to check.
 * @returns `true` if the value is not null or undefined, `false` otherwise.
 * @typeParam T - The type of the value.
 */
export function isNotNullOrUndefined<T>(value: T | undefined): value is T {
  return value !== null && value !== undefined;
}

/**
 * Transforms a type `T` to improve TypeScript's IntelliSense readability.
 *
 * The `Prettify<T>` type takes an input type `T` and maps over all its keys.
 * This process helps to reformat the type `T` such that it is easier to read
 * and understand when inspected using TypeScript's type analysis tools
 *
 * @template T - The input type to be prettified.
 *
 * Usage:
 * type Example = {
 *   id: number;
 *   name: string;
 *   data: {
 *     date: Date;
 *     flag: boolean;
 *   };
 * };
 *
 * type PrettyExample = Prettify<Example>;
 * // `PrettyExample` will now have the same structure as `Example`,
 * // but may appear cleaner in IntelliSense tooltips.
 */
export type Prettify<T> = {
  [K in keyof T]: T[K];
};

/**
 * A type that ensures an array is unique by adding a unique property to the array.
 *
 * @template T - The type of the array elements.
 *
 * Usage:
 * type UniqueArray<T> = T[] & { __unique: never };
 *
 * const uniqueArray: UniqueArray<string> = ['a', 'b', 'a']; // Error: Duplicate value 'a'
 * const uniqueArray: UniqueArray<string> = ['a', 'b', 'c']; // No error
 */
export type UniqueArray<T> = T[] & { __unique: never };
